import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import Fuse from "fuse.js";

import { FAQS } from "@/text/faqData";
import { saveLead } from "@/utils/saveLead";

const STORAGE_KEY = "zenia_chat_last_faq";

export default function ChatPanel({ onClose }) {
  const [tab, setTab] = useState("help");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(0);
  const [typing, setTyping] = useState(false);

  /* 🔥 CONTACT STATE */
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  /* LOAD LAST */
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved !== null) setOpen(Number(saved));
  }, []);

  useEffect(() => {
    if (open !== null) localStorage.setItem(STORAGE_KEY, open);
  }, [open]);

  /* ESC */
  useEffect(() => {
    const handler = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const fuse = useMemo(
    () =>
      new Fuse(FAQS, {
        keys: ["q", "a"],
        threshold: 0.35,
      }),
    []
  );

  const filtered = query ? fuse.search(query).map((r) => r.item) : FAQS;

  /* 🔥 FAQ CLICK */
  const handleOpen = (i) => {
    if (open === i) {
      setOpen(null);
      return;
    }

    setTyping(true);
    setOpen(i);

    setTimeout(() => setTyping(false), 400);
  };

  /* 🔥 FORM CHANGE */
  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  /* 🔥 SUBMIT */
  const handleSubmit = async () => {
    if (!form.name || !form.email || !accepted) return;

    try {
      setLoading(true);

      await saveLead({
        page: "chatbox",
        leadType: "chat_contact",
        source: "chat",
        leadDetails: form,
        consent: {
          termsAccepted: true,
          termsVersion: "15-04-2026",
          timestamp: Date.now(),
        },
      });

      setSuccess(true);
      setForm({ name: "", email: "", phone: "" });
      setAccepted(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.aside
      initial={{ opacity: 0, y: 16, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="
        fixed z-floating
        left-3 right-3 bottom-3
        sm:left-auto sm:right-4 sm:bottom-20
        sm:w-[340px]
        w-auto
        h-[min(80dvh,520px)]
        sm:h-[500px]
        max-h-[calc(100dvh-24px)]
        sm:max-h-[calc(100dvh-120px)]
        rounded-2xl
        bg-bg-surface
        border border-border-soft
        shadow-card
        flex flex-col overflow-hidden
      "
    >
      {/* HEADER */}
      <div className="px-4 py-3 bg-brand-secondary text-white">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-[10px] uppercase text-white/70 tracking-wide">
              Zenia AI
            </p>
            <h3 className="text-sm text-white/70 font-semibold">
              Ask anything
            </h3>
          </div>

          <button onClick={onClose} aria-label="Close chat panel">
            <X size={18} />
          </button>
        </div>
      </div>

      {/* TABS */}
      <div className="px-3 py-3 border-b border-border-soft">
        <div className="flex gap-1 p-1 rounded-xl bg-bg-light">
          {["help", "contact"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`
                flex-1 h-9 rounded-lg text-sm font-medium transition
                ${
                  tab === t
                    ? "bg-brand-primary text-white"
                    : "text-text-muted hover:text-text-primary"
                }
              `}
            >
              {t === "help" ? "Your Query" : "Contact"}
            </button>
          ))}
        </div>
      </div>

      {/* BODY */}
      <div className="flex-1 overflow-y-auto px-4 py-4 overscroll-contain">
        <AnimatePresence mode="wait">
          {/* 🔥 HELP TAB */}
          {tab === "help" && (
            <motion.div key="help" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {/* SEARCH */}
              <div className="relative mb-4">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
                />

                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Ask anything..."
                  className="
                    w-full h-10 pl-9 pr-3
                    rounded-xl border border-border-soft
                    bg-bg-light text-sm
                    outline-none
                    focus:ring-2 focus:ring-brand-primary
                  "
                />
              </div>

              {/* CHAT FLOW */}
              <div className="space-y-3">
                {filtered.map((item, i) => {
                  const isOpen = open === i;

                  return (
                    <div key={i}>
                      {/* USER */}
                      <button
                        onClick={() => handleOpen(i)}
                        className="
                          w-full text-left text-sm
                          bg-bg-light px-3 py-2 rounded-xl
                          transition active:scale-[0.99]
                        "
                      >
                        {item.q}
                      </button>

                      {/* AI */}
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-2"
                          >
                            <div
                              className="
                                border border-border-soft
                                rounded-xl px-3 py-3 text-sm
                                text-text-muted
                              "
                            >
                              {typing ? "Typing..." : item.a}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* 🔥 CONTACT TAB */}
          {tab === "contact" && (
            <motion.div key="contact" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {!success ? (
                <>
                  <p className="text-sm text-text-muted mb-4">
                    Leave your details — we’ll reach out.
                  </p>

                  <div className="space-y-3">
                    {["name", "email", "phone"].map((f) => (
                      <input
                        key={f}
                        value={form[f]}
                        onChange={(e) => handleChange(f, e.target.value)}
                        placeholder={f}
                        className="
                          w-full h-10 px-3 rounded-xl
                          border border-border-soft
                          bg-bg-light text-sm
                          outline-none
                          focus:ring-2 focus:ring-brand-primary
                        "
                      />
                    ))}
                  </div>

                  {/* CONSENT */}
                  <label className="mt-3 flex items-start gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={accepted}
                      onChange={() => setAccepted((v) => !v)}
                      className="mt-1 accent-brand-primary"
                    />

                    <span className="text-xs text-text-muted leading-snug">
                      I accept the{" "}
                      <Link
                        to="/terms"
                        target="_blank"
                        className="text-brand-primary underline underline-offset-2"
                      >
                        Terms & Conditions
                      </Link>
                    </span>
                  </label>

                  <button
                    onClick={handleSubmit}
                    disabled={loading || !accepted}
                    className="
                      w-full mt-4 h-10 rounded-xl
                      bg-brand-primary text-white font-semibold
                      disabled:opacity-60 disabled:cursor-not-allowed
                      transition active:scale-[0.99]
                    "
                  >
                    {loading ? "Sending..." : "Send"}
                  </button>
                </>
              ) : (
                <div className="text-center mt-10">
                  <p className="text-sm text-text-primary font-medium">
                    ✅ Request received
                  </p>

                  <p className="text-xs text-text-muted mt-2">
                    Our team will contact you shortly.
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.aside>
  );
}